import type { UserLike } from '@/Ship/Helpers/fieldViews'

/** Колекція справ (`SPEC-011` §4.1): від неї й адреси картки, історії, завершення. */
export const ACTIVITY_REST_URL = '/services/activities'

/** Ключ ресурсу справи (`SPEC-002` §2.2). */
export const ACTIVITY_RESOURCE = 'Crm.Activity'

/**
 * Шухляда картки справи (`SPEC-011` §8.4, К-1): 900 px — блоки полями по
 * два (у легасі 600 px, для цього тісно); заголовок «Справа», у створенні —
 * «Нова справа». Ядро читає `drawer.title` (перегляд, редагування) і
 * `drawer.createTitle` (створення) з ресурсу; `OpenResourceCardTask` ресурсної
 * шухляди не бере — йому ті самі значення передають явно.
 */
export const ACTIVITY_DRAWER = { width: 900, title: 'Справа', createTitle: 'Нова справа' } as const

/** Довідник «Статуси справ» (`SPEC-007` §5.1) — плашки рядка статусів і поле фільтра. */
export const ACTIVITY_STATUS_RESOURCE = 'Setting.ListActivityStatus'

/** Довідник «Типи справ». */
export const ACTIVITY_TYPE_RESOURCE = 'Setting.ListActivityType'

/**
 * Ресурси власника справи й їхніх контактів — **ключами**, а не імпортом:
 * контейнери клієнта й перевізника для справи чужі (`SPEC-002` §2.1), а
 * ресурс за ключем ядро знаходить саме (`GetResourceTask`).
 */
export const OWNER_RESOURCES = {
    client: { owner: 'Crm.Client', contact: 'Crm.ClientContact' },
    carrier: { owner: 'Crm.Carrier', contact: 'Crm.CarrierContact' },
} as const

/** Чия справа: клієнта чи перевізника (`SPEC-011` І4 — рівно один). */
export type ActivityOwnerKind = keyof typeof OWNER_RESOURCES

/** Поле власника й поле його контакту в записі справи. */
export const OWNER_FIELDS: Record<ActivityOwnerKind, { owner: 'client' | 'carrier'; contact: 'clientContact' | 'carrierContact' }> = {
    client: { owner: 'client', contact: 'clientContact' },
    carrier: { owner: 'carrier', contact: 'carrierContact' },
}

/**
 * Права справи — `_permissions` із `?permissions=true` (`SPEC-011` §10).
 * `complete` бек дає лише «Новій» справі.
 */
export interface ActivityPermissions {
    read?: boolean
    update?: boolean
    delete?: boolean
    complete?: boolean
}

/** Вкладений рядок довідника (тип, статус): назва, а в статусу — код і колір. */
export interface ActivityDictionaryRef {
    '@id'?: string
    uuid?: string
    code?: string | null
    title?: string | null
    color?: string | null
}

/** Власник чи контакт у записі справи — лише назва й адреса (`:short:read`). */
export interface ActivityRelationRef {
    '@id'?: string
    uuid?: string
    title?: string | null
}

/** Справа так, як її віддають список і картка (`SPEC-011` §3.5). */
export interface ActivityRecord {
    '@id'?: string
    uuid?: string
    id?: number
    title?: string | null
    type?: ActivityDictionaryRef | null
    status?: ActivityDictionaryRef | null
    client?: ActivityRelationRef | null
    clientContact?: ActivityRelationRef | null
    carrier?: ActivityRelationRef | null
    carrierContact?: ActivityRelationRef | null
    responsible?: UserLike | null
    deadline?: string | null
    description?: string | null
    result?: string | null
    isOverdue?: boolean
    isRemoved?: boolean
    doneBy?: UserLike | null
    doneAt?: string | null
    externalCode?: string | null
    _permissions?: ActivityPermissions
    /** Віртуальне поле форми (`SPEC-011` §8.4, К-5) — на бек не йде */
    ownerType?: ActivityOwnerKind
    /** Справа з картки власника: власника не перемикають (К-5, В8) — на бек не йде */
    ownerFixed?: boolean
    [key: string]: unknown
}

/** Те, що шаблон і його частини беруть із ядрового `formContext` картки. */
export interface ActivityFormContextLike {
    isEditable?: boolean
    values?: ActivityRecord
    setValue?: (path: string, value: unknown) => void
    setInitialValue?: (path: string, value: unknown) => void
    setInitialValues?: (values: Record<string, unknown>) => void
    setStorage?: (key: string, value: unknown) => void
}

/** Те, що картка бере з ядрового `crudContext`. */
export interface ActivityCrudContextLike {
    resource?: { key?: string; restUrl?: string; fields?: Record<string, unknown> }
    formContext?: ActivityFormContextLike
    actions?: {
        view?: (item: { uuid: string }) => unknown
        reloadData?: () => unknown
    }
    listCrudContext?: { listContext?: { actions?: { load?: (force?: boolean) => unknown } } }
}

/** Відповідь колекції ядра. */
export interface ActivityCollectionResponse {
    items?: ActivityRecord[]
    'hydra:member'?: ActivityRecord[]
    meta?: { total?: number }
}

/** Відповідь `GET /services/activities/counters` (`SPEC-011` §4.4). */
export interface ActivityCountersResponse {
    total?: number
    byStatus?: Record<string, number>
}
